// src/components/reports/ReportsDashboard.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Package } from 'lucide-react'
import reportsService from '../../services/reportsService'

const KPICard = ({ title, value, change, trend, icon: Icon, color }) => {
  const isPositive = trend === 'up'
  const TrendIcon = isPositive ? TrendingUp : TrendingDown
  
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {change && (
              <div className={`flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                <TrendIcon className="h-4 w-4 mr-1" />
                <span>{change}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const QuickInsight = ({ title, insight, type }) => {
  const getInsightColor = (type) => {
    switch (type) {
      case 'positive': return 'text-green-700 bg-green-50 border-green-200'
      case 'warning': return 'text-yellow-700 bg-yellow-50 border-yellow-200'
      case 'negative': return 'text-red-700 bg-red-50 border-red-200'
      default: return 'text-blue-700 bg-blue-50 border-blue-200'
    }
  }

  return (
    <div className={`p-4 rounded-lg border ${getInsightColor(type)}`}>
      <h4 className="font-semibold mb-2">{title}</h4>
      <p className="text-sm">{insight}</p>
    </div>
  )
}

export const ReportsDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      
      // Load key metrics
      const [salesData, paymentsData, productsData, customersData] = await Promise.all([
        reportsService.generateSalesSummaryReport(),
        reportsService.generatePaymentsReport(),
        reportsService.generateProductSummaryReport(),
        reportsService.generateCustomersReport()
      ])

      // Generate insights
      const insights = generateInsights(salesData, paymentsData, productsData)
      
      setDashboardData({
        sales: salesData,
        payments: paymentsData,
        products: productsData,
        customers: customersData,
        insights
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateInsights = (sales, payments, products) => {
    const insights = []
    
    // Sales insights
    if (sales.summary.totalOrders > 0) {
      const avgOrderValue = sales.summary.avgOrderValue
      if (avgOrderValue > 1000) {
        insights.push({
          type: 'success',
          title: 'High Average Order Value',
          insight: `Your average order value is Rs ${avgOrderValue.toFixed(2)}, which is excellent!`,
        })
      } else if (avgOrderValue < 500) {
        insights.push({
          title: 'Opportunity to Increase Sales',
          insight: `Consider upselling or bundling to increase the current Rs ${avgOrderValue.toFixed(2)} average order value.`,
          type: 'warning'
        })
      }
    }

    // Payment method insights
    if (payments.data.length > 0) {
      const cashPercentage = payments.data.find(p => p.paymentMethod === 'Cash')?.percentage || 0
      if (cashPercentage > 70) {
        insights.push({
          title: 'High Cash Transactions',
          insight: `${cashPercentage.toFixed(1)}% of transactions are cash. Consider promoting digital payments.`,
          type: 'info'
        })
      }
    }

    // Stock alerts
    const lowStockProducts = products.data.filter(p => p.status === 'Low Stock').length
    if (lowStockProducts > 5) {
      insights.push({
        title: 'Stock Alert',
        insight: `${lowStockProducts} products are running low on stock. Consider reordering soon.`,
        type: 'warning'
      })
    }

    return insights
  }

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
    return `Rs ${formatted}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Unable to load dashboard data</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Revenue"
          value={formatCurrency(dashboardData.sales.summary.totalRevenue)}
          icon={DollarSign}
          color="text-green-600"
        />
        <KPICard
          title="Total Orders"
          value={dashboardData.sales.summary.totalOrders.toLocaleString()}
          icon={ShoppingCart}
          color="text-blue-600"
        />
        <KPICard
          title="Average Order"
          value={formatCurrency(dashboardData.sales.summary.avgOrderValue)}
          icon={TrendingUp}
          color="text-purple-600"
        />
        <KPICard
          title="Total Customers"
          value={dashboardData.customers.summary.totalCustomers.toLocaleString()}
          icon={Users}
          color="text-orange-600"
        />
      </div>

      {/* Quick Insights */}
      {dashboardData.insights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>📊 Quick Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboardData.insights.map((insight, index) => (
                <QuickInsight
                  key={index}
                  title={insight.title}
                  insight={insight.insight}
                  type={insight.type}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              💳 Payment Methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.payments.data.slice(0, 3).map((payment, index) => (
              <div key={index} className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">{payment.paymentMethod}</span>
                <div className="text-right">
                  <div className="font-semibold">{formatCurrency(payment.totalAmount)}</div>
                  <div className="text-xs text-gray-500">{payment.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              📦 Top Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.products.data.slice(0, 3).map((product, index) => (
              <div key={index} className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600 truncate">{product.productName}</span>
                <div className="text-right">
                  <div className="font-semibold">Stock: {product.stockLevel}</div>
                  <div className={`text-xs ${product.status === 'In Stock' ? 'text-green-600' : 'text-red-600'}`}>
                    {product.status}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              🎯 Key Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Products Count</span>
                <span className="font-semibold">{dashboardData.products.summary.totalProducts}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Active Customers</span>
                <span className="font-semibold">{dashboardData.customers.summary.activeCustomers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Payment Methods</span>
                <span className="font-semibold">{dashboardData.payments.summary.methodsCount}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ReportsDashboard